import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { Grid, Box, Typography, Button, CircularProgress } from '@mui/material';
import { Print as IconPrint, ArrowBack as IconBack } from "@mui/icons-material";

import comprobanteService from "../../services/comprobanteService";
import comprobantePagoService from "../../services/comprobantePagoService";

// Reporte de impresion del comprobante. El formato sigue los estandares de
// impresion de comprobantes: cabecera con la serie y numero, datos del pagador,
// detalle de los conceptos, pagos y el cuadro de totales al pie.
const Print = (props) => {
    const params = useParams();
    const navigate = useNavigate();

    const [cargando, setCargando] = useState(true);
    const [comprobante, setComprobante] = useState(null);
    const [detalles, setDetalles] = useState([]);
    const [pagos, setPagos] = useState([]);

    const fetchDatos = useCallback(async () => {
        setCargando(true);

        const comprobanteResponse = await comprobanteService.getComprobanteView(params.id)
            .catch((err) => { console.log("Err: ", err) });

        const entity = comprobanteResponse?.data?.data;

        if (!entity) {
            setCargando(false);
            return;
        }

        setComprobante(entity);

        const responseDetalles = await comprobanteService.getsComprobanteDetalleView(params.id)
            .catch((err) => { console.log("Err: ", err) });

        setDetalles(responseDetalles?.data?.data || []);

        const responsePagos = await comprobantePagoService.getsComprobantePagoView(params.id)
            .catch((err) => { console.log("Err: ", err) });

        setPagos(responsePagos || []);

        setCargando(false);
    }, [params.id])

    useEffect(() => {
        fetchDatos();
    }, [fetchDatos])

    const serieNumero = comprobante?.serie && comprobante?.numero
        ? comprobante.serie + "-" + String(parseInt(comprobante.numero)).padStart(6, "0")
        : "";

    const total = (detalles || []).reduce((sum, item) => sum + (parseFloat(item.subTotal) || 0), 0);
    const totalPago = (pagos || []).reduce((sum, item) => sum + (parseFloat(item.monto) || 0), 0);
    const saldo = Math.max(total - totalPago, 0);

    const simbolo = comprobante?.moneda == "Dólares" ? "US$" : "S/";
    const monto = (value) => simbolo + " " + (parseFloat(value) || 0).toFixed(2);

    return (
        <Box className="pantalla-impresion">
            <Box className="solo-impresion">
                <Grid container spacing={1} alignItems="center" sx={{ mb: 2 }}>
                    <Grid item xs={12}>
                        <Button variant="outlined" size="small" startIcon={<IconBack />}
                            onClick={() => navigate("/receipt")}>
                            Volver
                        </Button>
                    </Grid>
                    <Grid item xs={12} display={"flex"} justifyContent={"flex-end"}>
                        <Button variant="contained" size="small" startIcon={<IconPrint />}
                            onClick={() => window.print()}>
                            Imprimir
                        </Button>
                    </Grid>
                </Grid>
            </Box>

            {cargando
                ? <Box display={"flex"} justifyContent={"center"} sx={{ my: 6 }}><CircularProgress /></Box>
                : !comprobante
                    ? <Typography>No se encontro el comprobante.</Typography>
                    : <Box className="comprobante-impresion">
                        {/* Encabezado */}
                        <Box textAlign={"center"} mb={1}>
                            <Typography variant="h5" sx={{ fontWeight: 'bold', letterSpacing: 1 }}>
                                {(comprobante.tipo || "").toUpperCase()}
                            </Typography>
                            <Typography variant="subtitle1">
                                {serieNumero}
                            </Typography>
                            <Typography variant="body2">
                                Fecha de emision: {moment(comprobante.fecha).format('DD/MM/YYYY')}
                            </Typography>
                        </Box>

                        {/* Datos del comprobante */}
                        <Grid container spacing={1} sx={{ borderTop: "1px solid #000", borderBottom: "1px solid #000", py: 1 }}>
                            <Grid item xs={6}>
                                <Typography variant="body2">
                                    <b>Cobrador:</b> {comprobante.cobrador || ""}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="body2">
                                    <b>Pagador:</b> {comprobante.pagador || ""}
                                </Typography>
                            </Grid>
                            {comprobante.dniPagador && (
                                <Grid item xs={6}>
                                    <Typography variant="body2">
                                        <b>Documento:</b> {comprobante.dniPagador}
                                    </Typography>
                                </Grid>
                            )}
                            <Grid item xs={6}>
                                <Typography variant="body2">
                                    <b>Moneda:</b> {comprobante.moneda || ""} {comprobante.tipoCambio ? "| Tipo de cambio: " + comprobante.tipoCambio : ""}
                                </Typography>
                            </Grid>
                        </Grid>

                        {/* Detalle de conceptos */}
                        <Box mt={1}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Detalle de los conceptos</Typography>
                            <table className="tabla-impresion">
                                <thead>
                                    <tr>
                                        <th style={{ width: "10%" }}>Cant.</th>
                                        <th style={{ width: "45%" }}>Concepto</th>
                                        <th style={{ width: "20%" }}>P. Unitario</th>
                                        <th style={{ width: "25%" }}>Sub Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {detalles.map((row, index) => (
                                        <tr key={index}>
                                            <td className="centro">{row.cantidad}</td>
                                            <td>{row.conceptoCobro}</td>
                                            <td className="derecha">{monto(row.precioUnitario)}</td>
                                            <td className="derecha">{monto(row.subTotal)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </Box>

                        {/* Pagos */}
                        <Box mt={1}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Pagos</Typography>
                            <table className="tabla-impresion">
                                <thead>
                                    <tr>
                                        <th style={{ width: "25%" }}>Medio de pago</th>
                                        <th style={{ width: "20%" }}>Entidad</th>
                                        <th style={{ width: "20%" }}>N. operacion</th>
                                        <th style={{ width: "15%" }}>Fecha</th>
                                        <th style={{ width: "20%" }}>Monto</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pagos.map((row, index) => (
                                        <tr key={index}>
                                            <td>{row.medioPago}</td>
                                            <td>{row.entidadPago}</td>
                                            <td>{row.numeroOperacion}</td>
                                            <td className="centro">{moment(row.fechaPago).format('DD/MM/YYYY')}</td>
                                            <td className="derecha">{monto(row.monto)}</td>
                                        </tr>
                                    ))}
                                    {pagos.length === 0 && (
                                        <tr>
                                            <td colSpan={5} className="centro">Sin pagos registrados</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </Box>

                        {/* Totales */}
                        <Box display={"flex"} justifyContent={"flex-end"} mt={1}>
                            <table className="tabla-impresion" style={{ width: "45%" }}>
                                <tbody>
                                    <tr>
                                        <td><b>Total</b></td>
                                        <td className="derecha"><b>{monto(total)}</b></td>
                                    </tr>
                                    <tr>
                                        <td>Total pagado</td>
                                        <td className="derecha">{monto(totalPago)}</td>
                                    </tr>
                                    <tr>
                                        <td><b>Saldo</b></td>
                                        <td className="derecha"><b>{monto(saldo)}</b></td>
                                    </tr>
                                </tbody>
                            </table>
                        </Box>

                        {/* Observaciones y estado */}
                        <Grid container spacing={1} sx={{ mt: 1, borderTop: "1px solid #000", pt: 1 }}>
                            <Grid item xs={12}>
                                <Typography variant="body2">
                                    <b>Observaciones:</b> {comprobante.observaciones || ""}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="body2">
                                    <b>Estado del comprobante:</b> {comprobante.estado || ""}
                                </Typography>
                            </Grid>
                            <Grid item xs={6} textAlign={"right"}>
                                <Typography variant="body2">
                                    <b>Estado del pago:</b> {comprobante.estadoPago || ""}
                                </Typography>
                            </Grid>
                        </Grid>

                        <Typography variant="body2" textAlign={"center"} sx={{ mt: 2 }}>
                            Representacion impresa del comprobante electronico
                        </Typography>
                    </Box>
            }

            {/* Al imprimir solo sale el comprobante */}
            <style>{`
                @media print {
                    .solo-impresion { display: none !important; }
                    .MuiDrawer-root, .MuiAppBar-root { display: none !important; }
                    .comprobante-impresion { font-family: Arial, Helvetica, sans-serif; font-size: 12px; color: #000; }
                    .tabla-impresion { width: 100%; border-collapse: collapse; margin-top: 4px; }
                    .tabla-impresion th, .tabla-impresion td { border: 1px solid #000; padding: 3px 6px; }
                    .tabla-impresion th { text-align: left; background: #eee; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                    .tabla-impresion .derecha { text-align: right; }
                    .tabla-impresion .centro { text-align: center; }
                }
            `}</style>
        </Box>
    )
}

export default Print;